/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Typography2 } from '@coursera/cds-core';
import { track } from '@coursera/event-pulse/sdk';

import MobileButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileButton';
import type { MobileExploreIdentifierValues } from 'bundles/composable-page-header/constants';
import {
  MOBILE_EXPLORE_IDENTIFIER,
  browseAllSubjectsButtonProps,
  domainButtonProps,
  goalsButtonsProps,
} from 'bundles/composable-page-header/constants';
import { useMegaMenuData } from 'bundles/composable-page-header/providers/MegaMenuDataProvider';
import type { MenuItem } from 'bundles/megamenu/types/MenuData';

import _t from 'i18n!nls/composable-page-header';

const styles = {
  goals: css`
    padding: var(--cds-spacing-300) 0 var(--cds-spacing-100) 0;
  `,
  subjects: css`
    margin-top: var(--cds-spacing-150);
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    padding: var(--cds-spacing-300) 0 var(--cds-spacing-100) 0;
  `,
};

type Props = {
  setExploreViewState: (state: MobileExploreIdentifierValues) => void;
  setMenuItem: (item: MenuItem | undefined) => void;
};

const ExploreGoalsAndDomains = ({ setExploreViewState, setMenuItem }: Props) => {
  const goalItems = goalsButtonsProps();
  const megaMenuData = useMegaMenuData();
  const goalsSectionMenu = megaMenuData?.[0]?.sectionMenus;
  const domainSectionMenu = megaMenuData?.[1]?.sectionMenus;

  return (
    <div data-testid="exploreGoalsAndDomains">
      <Typography2 variant="subtitleMedium" component="h3" css={styles.goals}>
        {_t('Goals')}
      </Typography2>
      <MobileButton {...goalItems.freeCourse} />
      {goalsSectionMenu &&
        [goalItems.degree, goalItems.cert].map((item) => (
          <MobileButton
            key={`mobileNav-${item.name}`}
            name={item.name}
            trackingName={item.trackingName}
            onButtonClick={() => {
              item.onButtonClick();
              setExploreViewState(item.exploreViewState);
              setMenuItem(goalsSectionMenu.find((menuItem) => menuItem.id === item.name));
            }}
            label={item.label}
            ariaLabel={item.ariaLabel}
          />
        ))}
      <MobileButton {...goalItems.career} />
      <Typography2 variant="subtitleMedium" component="h3" css={styles.subjects}>
        {_t('Subjects')}
      </Typography2>
      {domainSectionMenu &&
        domainButtonProps().map((item) => (
          <MobileButton
            key={`mobileNav-${item.name}`}
            name={item.name}
            trackingName="mobile_header_domains_button"
            aria-label={_t('Go to #{domainName} domain navigation slide', { domainName: item.label })}
            label={item.label}
            onButtonClick={() => {
              track('view_megamenu_items', {
                megamenuItemName: item.name,
                megamenuSection: 'subjects',
              });
              setExploreViewState(MOBILE_EXPLORE_IDENTIFIER.DOMAIN_MENU);
              setMenuItem(domainSectionMenu.find((menuItem) => menuItem.id === item.name));
            }}
          />
        ))}
      <MobileButton {...browseAllSubjectsButtonProps()} />
    </div>
  );
};

export default ExploreGoalsAndDomains;
